import queryString from 'query-string';
import { useLocation } from 'react-router';

import { Icon } from '#/components/Icon/Icon';
import { BodySmall } from '#/components/Text/BodySmall';
import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors, ViewportBreakpoint } from '#/theme/theme';

import { getResultsDescription } from './getResultsDescription';
import { Page } from './Page';
import {
  ArrowLink,
  Pagination_Box_1,
  Pagination_Box_2,
  Pagination_Box_3,
  Pagination_Box_4,
  Pagination_Hide_1,
} from './Pagination.styled';

type Props = {
  nbHits: number;
  nbPages: number;
  hitsPerPage: number;
  pageIndex: number;
  onChangePageIndex?: (pageIndex: number) => void;
  pageParam?: string;
};

export function Pagination({
  nbHits,
  nbPages,
  hitsPerPage,
  pageIndex,
  onChangePageIndex,
  pageParam = 'page',
}: Props) {
  const { i18n } = useI18n();

  const navigate = useDeferredNavigate();
  const location = useLocation();
  const PAGINATION_RADIUS =
    useViewportBreakpoint() === ViewportBreakpoint.xs ? 1 : 2;
  if (nbPages < 2) return null;

  const pageLink = (index: number) => {
    const params = queryString.parse(location.search);

    if (index > 0) {
      // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'string | ... Remove this comment to see the full error message
      params[pageParam] = index + 1;
    } else {
      delete params[pageParam];
    }

    return `?${queryString.stringify(params)}`;
  };

  const goToPage = (index: number) => {
    void navigate(pageLink(index));
    onChangePageIndex?.(index);
  };

  // Total number of page links to show
  const pageLinkCount = PAGINATION_RADIUS * 2 + 1;
  const allPages = [];

  for (let i = 0; i < nbPages; i++) {
    allPages.push(i);
  }

  const startIndex = Math.max(pageIndex - PAGINATION_RADIUS, 0);
  const endIndex = startIndex + pageLinkCount;
  const pageLinksToShow =
    endIndex > nbPages // We are at the end of the list
      ? allPages.slice(-pageLinkCount) // We are in the middle or beginning of list
      : allPages.slice(startIndex, endIndex);

  const chevronLeftDisabled = pageIndex === 0;
  const chevronRightDisabled = pageIndex === nbPages - 1;

  return (
    <div
      style={{
        marginTop: '18px',
        paddingTop: '18px',
        borderTop: `1px solid ${colors.selectionGrey}`,
      }}
    >
      <Pagination_Box_4>
        <BodySmall style={{ lineHeight: '1' }}>
          {getResultsDescription({
            i18n,
            pageIndex,
            hitsPerPage,
            nbHits,
          })}
        </BodySmall>
        <Pagination_Box_3 data-qa-id="pagination">
          <ArrowLink
            qaId="pagination-link-previous"
            href={pageLink(pageIndex - 1)}
            $disabled={chevronLeftDisabled}
            tabIndex={chevronLeftDisabled ? -1 : 0}
            aria-label={i18n('Previous page')}
            onClick={(e) => {
              e.preventDefault();
              goToPage(pageIndex - 1);
            }}
          >
            <Icon name="chevron-left" size={16} />
          </ArrowLink>
          {pageLinksToShow[0] > 0 && (
            <>
              <Page
                key={0}
                pageNumber={0}
                href={pageLink(0)}
                selected={false}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(0);
                }}
              />

              {pageLinksToShow[0] > 1 && (
                <Pagination_Box_2>...</Pagination_Box_2>
              )}
            </>
          )}
          {pageLinksToShow.map((pageNumber) => (
            <Pagination_Hide_1
              key={pageNumber} // only show 3 pages on small mobile screens
              // otherwise paging will wrap on iPhone SE
              data-hide-xs={
                pageNumber !== pageIndex &&
                pageNumber !== nbPages - 1 &&
                (pageNumber === pageLinksToShow[0] ||
                  pageNumber === pageLinksToShow[pageLinksToShow.length - 1])
                  ? true
                  : undefined
              }
            >
              <Page
                key={pageNumber}
                href={pageLink(pageNumber)}
                pageNumber={pageNumber}
                selected={pageNumber === pageIndex}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(pageNumber);
                }}
              />
            </Pagination_Hide_1>
          ))}
          {pageLinksToShow[pageLinksToShow.length - 1] < nbPages - 1 && (
            <>
              {pageLinksToShow[pageLinksToShow.length - 1] < nbPages - 2 && (
                <Pagination_Box_1>...</Pagination_Box_1>
              )}
              <Page
                key={nbPages - 1}
                href={pageLink(nbPages - 1)}
                pageNumber={nbPages - 1}
                selected={false}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(nbPages - 1);
                }}
              />
            </>
          )}
          <ArrowLink
            qaId="pagination-link-next"
            href={pageLink(pageIndex + 1)}
            $disabled={chevronRightDisabled}
            tabIndex={chevronRightDisabled ? -1 : 0}
            onClick={(e) => {
              e.preventDefault();
              goToPage(pageIndex + 1);
            }}
            aria-label={i18n('Next page')}
          >
            <Icon name="chevron-right" size={16} />
          </ArrowLink>
        </Pagination_Box_3>
      </Pagination_Box_4>
    </div>
  );
}
